import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { initialApplications } from "../mock/applications";
import { initialMilestoneTemplates } from "../mock/milestoneTemplates";
import { initialGeoZones } from "../mock/geoZones";
import { initialConstructionTimelinePhases } from "../mock/constructionTimelinePhases";
import { initialStandardResources } from "../mock/standardResources";
import { initialStoreYards } from "../mock/storeYards";
import { initialSubcontractorMasters } from "../mock/subcontractorMasters";
import type {
  AgentMaster,
  AgentMasterUpsertPayload,
  Application,
  ApplicationDossierPatch,
  ConstructionTimelinePhase,
  EngineerLinkage,
  EngineerMaster,
  GeoZone,
  HoBankingPatch,
  HoFields,
  ValidationsPatch,
  MilestoneItemKind,
  MilestoneTemplate,
  StandardResource,
  StandardResourceItem,
  StoreYard,
  SubcontractorLinkage,
  SubcontractorMaster,
  Validations,
  WorkDoneBill,
} from "../types";
import { normalizeLinkedResourceIds } from "../utils/milestoneResourceLinks";
import { normalizeLinkedTimelinePhaseIds } from "../utils/milestoneTimelineLinks";
import { syncMilestoneProgress } from "../utils/milestoneSync";
import {
  deriveApplicationStatus,
  findSubcontractorMaster,
  isSubcontractorLinkageComplete,
} from "../utils/subcontractor";
import {
  emptyEngineerLinkage,
  findEngineerMaster,
  isEngineerLinkageComplete,
} from "../utils/engineer";
import { canAnyInventory } from "../config/consolePermissionKeys";
import { useToast } from "./ToastContext";
import { canAccessApplicationsHub, canMutateApplicantDossiers } from "../config/applicationHubPermissions";
import { filterStoreYardsByConsoleScope } from "../utils/inventoryStoreScope";
import { useAuth } from "./AuthContext";
import type { ConstructionTimelinePhaseDto } from "../api/constructionTimelineApi";
import {
  createConstructionTimelinePhase as apiCreateConstructionTimelinePhase,
  deleteConstructionTimelinePhaseApi,
  fetchConstructionTimelinePhases,
  reorderConstructionTimelinePhases,
  updateConstructionTimelinePhaseApi,
} from "../api/constructionTimelineApi";
import {
  createStandardResourceApi,
  createStandardResourceItemApi,
  deleteStandardResourceApi,
  deleteStandardResourceItemApi,
  fetchStandardResources,
  reorderStandardResources,
  updateStandardResourceApi,
  updateStandardResourceItemApi,
} from "../api/standardResourcesApi";
import {
  createSubcontractorMasterApi,
  deleteSubcontractorMasterApi,
  fetchSubcontractorMasters,
  updateSubcontractorMasterApi,
} from "../api/subcontractorMastersApi";
import {
  createEngineerMasterApi,
  deleteEngineerMasterApi,
  fetchEngineerMasters,
  updateEngineerMasterApi,
} from "../api/engineerMastersApi";
import { createGeoZoneApi, deleteGeoZoneApi, fetchGeoZones, updateGeoZoneApi } from "../api/geoZonesApi";
import {
  createStoreYardApi,
  deleteStoreYardApi,
  fetchStoreYards,
  reorderStoreYards,
  updateStoreYardApi,
} from "../api/storeYardsApi";
import {
  createMilestoneTemplateApi,
  createMilestoneTemplateItemApi,
  deleteMilestoneTemplateApi,
  deleteMilestoneTemplateItemApi,
  fetchMilestoneTemplates,
  updateMilestoneTemplateApi,
} from "../api/milestoneTemplatesApi";
import {
  fetchApplications,
  patchApplicationDossier,
  patchApplicationHoBanking,
  patchApplicationValidations,
  saveApplicationById,
  saveApplicationsAll,
  saveMilestoneComplete,
} from "../api/applicationsApi";
import { applicationPersistFingerprint } from "../utils/applicationPersistFingerprint";
import { createAgentApi, deleteAgentApi, fetchAgents, sendAgentOnboardingEmailApi, updateAgentApi } from "../api/agentsApi";

function phaseFromTimelineDto(d: ConstructionTimelinePhaseDto): ConstructionTimelinePhase {
  return {
    id: String(d.id),
    title: d.title,
    approxDays: d.approxDays,
    sortOrder: d.sortOrder,
    createdAt: d.createdAt,
  };
}

type AppRootState = {
  applications: Application[];
  subcontractorMasters: SubcontractorMaster[];
  engineerMasters: EngineerMaster[];
  agentMasters: AgentMaster[];
  milestoneTemplates: MilestoneTemplate[];
  geoZones: GeoZone[];
  storeYards: StoreYard[];
  constructionTimelinePhases: ConstructionTimelinePhase[];
  standardResources: StandardResource[];
};

function withDerivedStatuses(apps: Application[], masters: SubcontractorMaster[]): Application[] {
  return apps.map((a) => ({ ...a, status: deriveApplicationStatus(a, masters) }));
}

function finalizeDraft(draft: AppRootState): AppRootState {
  const synced = syncMilestoneProgress(draft.applications, draft.milestoneTemplates);
  const applications = withDerivedStatuses(synced, draft.subcontractorMasters);
  return {
    milestoneTemplates: draft.milestoneTemplates,
    subcontractorMasters: draft.subcontractorMasters,
    engineerMasters: draft.engineerMasters,
    agentMasters: draft.agentMasters,
    geoZones: draft.geoZones,
    storeYards: draft.storeYards,
    constructionTimelinePhases: draft.constructionTimelinePhases,
    standardResources: draft.standardResources,
    applications,
  };
}

function initialRoot(): AppRootState {
  return finalizeDraft({
    applications: [...initialApplications],
    subcontractorMasters: [...initialSubcontractorMasters],
    engineerMasters: [],
    agentMasters: [],
    milestoneTemplates: [...initialMilestoneTemplates],
    geoZones: [...initialGeoZones],
    storeYards: [...initialStoreYards],
    constructionTimelinePhases: [...initialConstructionTimelinePhases],
    standardResources: [...initialStandardResources],
  });
}

interface AppStateContextValue {
  applications: Application[];
  subcontractorMasters: SubcontractorMaster[];
  engineerMasters: EngineerMaster[];
  agentMasters: AgentMaster[];
  milestoneTemplates: MilestoneTemplate[];
  geoZones: GeoZone[];
  storeYards: StoreYard[];
  constructionTimelinePhases: ConstructionTimelinePhase[];
  standardResources: StandardResource[];
  getById: (id: string) => Application | undefined;
  getSubcontractorMaster: (id: string) => SubcontractorMaster | undefined;
  getEngineerMaster: (id: string) => EngineerMaster | undefined;
  getAgentMaster: (id: string) => AgentMaster | undefined;
  getMilestoneTemplate: (id: string) => MilestoneTemplate | undefined;
  getStandardResource: (id: string) => StandardResource | undefined;
  updateApplication: (id: string, patch: Partial<Application>) => Promise<void>;
  updateHo: (id: string, ho: Partial<HoFields>) => void;
  updateValidations: (id: string, v: Partial<Validations>) => Promise<void>;
  setHoldReasons: (id: string, reasons: string[]) => Promise<void>;
  confirmSeeding: (id: string) => Promise<void>;
  saveIciciDetails: (id: string, patch: HoBankingPatch) => Promise<void>;
  addWorkDoneBill: (id: string, fileName: string) => Promise<void>;
  confirmBillSeeding: (id: string, billId: string) => Promise<void>;
  updateSubcontractor: (id: string, partial: Partial<SubcontractorLinkage>) => Promise<void>;
  finalizeSubcontractor: (
    id: string,
    filePayload?: { workOrderFileBase64: string; workOrderMimeType: string | null },
  ) => Promise<boolean>;
  updateEngineer: (id: string, partial: Partial<EngineerLinkage>) => Promise<void>;
  finalizeEngineer: (id: string) => Promise<boolean>;
  addSubcontractorMaster: (row: Omit<SubcontractorMaster, "id" | "createdAt">) => Promise<string>;
  updateSubcontractorMaster: (
    id: string,
    patch: Partial<Omit<SubcontractorMaster, "id" | "createdAt">>,
  ) => Promise<boolean>;
  deleteSubcontractorMaster: (id: string) => Promise<boolean>;
  addEngineerMaster: (row: Omit<EngineerMaster, "id" | "createdAt">) => Promise<string>;
  updateEngineerMaster: (id: string, patch: Partial<Omit<EngineerMaster, "id" | "createdAt">>) => Promise<boolean>;
  deleteEngineerMaster: (id: string) => Promise<boolean>;
  addAgentMaster: (row: AgentMasterUpsertPayload) => Promise<string>;
  updateAgentMaster: (id: string, patch: Partial<AgentMasterUpsertPayload>) => Promise<boolean>;
  deleteAgentMaster: (id: string) => Promise<boolean>;
  sendAgentOnboardingEmail: (id: string, options?: { customPasswordForEmail?: string }) => Promise<boolean>;
  addMilestoneTemplate: (input: {
    title: string;
    notes?: string;
    items: { kind: MilestoneItemKind; label: string }[];
      linkedConstructionTimelinePhaseIds?: string[];
      linkedResourceIds?: string[];
    }) => Promise<boolean>;
  updateMilestoneTemplate: (
    id: string,
    patch: Partial<
      Pick<
        MilestoneTemplate,
        "title" | "notes" | "sortOrder" | "linkedConstructionTimelinePhaseIds" | "linkedResourceIds"
      >
    >,
  ) => Promise<void>;
  removeMilestoneTemplate: (id: string) => Promise<boolean>;
  addMilestoneTemplateItem: (templateId: string, item: { kind: MilestoneItemKind; label: string }) => Promise<boolean>;
  removeMilestoneTemplateItem: (templateId: string, itemId: string) => Promise<void>;
  setMilestonePhaseComplete: (applicationId: string, templateId: string, done: boolean) => void;
  setMilestoneItemBillLink: (
    applicationId: string,
    templateId: string,
    templateItemId: string,
    workDoneBillId: string | null,
  ) => Promise<void>;
  setMilestoneItemDone: (
    applicationId: string,
    templateId: string,
    templateItemId: string,
    done: boolean,
    workDoneBillId?: string | null,
  ) => Promise<void>;
  addGeoZone: (input: { name: string; notes?: string }) => Promise<string>;
  updateGeoZone: (
    id: string,
    patch: Partial<
      Pick<GeoZone, "name" | "notes" | "districtKeys" | "mandalRefs" | "villageRefs" | "sortOrder">
    >,
  ) => Promise<void>;
  removeGeoZone: (id: string) => Promise<void>;
  addStoreYard: (input: { name: string; notes?: string; address?: string }) => Promise<string>;
  updateStoreYard: (
    id: string,
    patch: Partial<
      Pick<
        StoreYard,
        | "name"
        | "notes"
        | "address"
        | "scopeType"
        | "districtKeys"
        | "mandalRefs"
        | "villageRefs"
        | "zoneIds"
        | "sortOrder"
      >
    >,
  ) => Promise<void>;
  removeStoreYard: (id: string) => Promise<boolean>;
  moveStoreYard: (id: string, direction: "up" | "down") => Promise<void>;
  addConstructionTimelinePhase: (input: { title: string; approxDays: number }) => Promise<string>;
  updateConstructionTimelinePhase: (
    id: string,
    patch: Partial<Pick<ConstructionTimelinePhase, "title" | "approxDays" | "sortOrder">>,
  ) => Promise<void>;
  removeConstructionTimelinePhase: (id: string) => Promise<boolean>;
  moveConstructionTimelinePhase: (id: string, direction: "up" | "down") => Promise<void>;
  addStandardResource: (input: { title: string; notes?: string }) => Promise<string>;
  updateStandardResource: (
    id: string,
    patch: Partial<Pick<StandardResource, "title" | "notes" | "sortOrder">>,
  ) => Promise<void>;
  removeStandardResource: (id: string) => Promise<boolean>;
  moveStandardResource: (id: string, direction: "up" | "down") => Promise<void>;
  addStandardResourceItem: (
    resourceId: string,
    item: {
      label: string;
      quantity: number;
      unit: string;
      rate?: number;
      hsnCode?: string;
      catalogStandardItemId?: string | null;
    },
  ) => Promise<boolean>;
  updateStandardResourceItem: (
    resourceId: string,
    itemId: string,
    patch: Partial<Pick<StandardResourceItem, "label" | "quantity" | "unit" | "rate" | "hsnCode">>,
  ) => Promise<void>;
  removeStandardResourceItem: (resourceId: string, itemId: string) => Promise<void>;
  /** Re-fetch applicants from the API (e.g. after subcontractor timeline sync updates actual days). */
  refreshApplicationsFromApi: () => Promise<void>;
}

const AppStateContext = createContext<AppStateContextValue | null>(null);

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const { can, loading: authLoading, user, authRequired } = useAuth();

  const requireApplicantDossierMutation = useCallback((): boolean => {
    if (!canMutateApplicantDossiers(can)) {
      showToast("You do not have permission to save applicant dossier changes.", "error");
      return false;
    }
    return true;
  }, [can, showToast]);
  const [root, setRoot] = useState<AppRootState>(initialRoot);
  const timelineApiActiveRef = useRef(false);
  const standardResourcesApiActiveRef = useRef(false);
  const subcontractorMastersApiActiveRef = useRef(false);
  const engineerMastersApiActiveRef = useRef(false);
  const agentMastersApiActiveRef = useRef(false);
  const geoZonesApiActiveRef = useRef(false);
  const storeYardsApiActiveRef = useRef(false);
  const milestoneTemplatesApiActiveRef = useRef(false);
  const applicationsApiActiveRef = useRef(false);
  const applicationsHydratedRef = useRef(false);
  const applicationsSkipNextPersistRef = useRef(false);
  /** Last persisted fingerprints per application id — used to send single-row PUT when only one dossier changed. */
  const lastApplicationFingerprintByIdRef = useRef(new Map<string, string>());
  const rootRef = useRef(root);
  useLayoutEffect(() => {
    rootRef.current = root;
  }, [root]);

  /** Full-table PUT must run strictly one-at-a-time; overlapping responses could otherwise restore stale `validations` (e.g. seeding cleared after ICICI save). */
  const applicationsPersistChainRef = useRef(Promise.resolve());

  const enqueueApplicationsPersist = useCallback((snapshot?: Application[]) => {
    applicationsPersistChainRef.current = applicationsPersistChainRef.current
      .catch(() => {
        /* keep queue alive after a failed save */
      })
      .then(async () => {
        if (!applicationsApiActiveRef.current) return;
        if (!canMutateApplicantDossiers(can)) return;
        const apps = snapshot ?? rootRef.current.applications;
        const prevFp = lastApplicationFingerprintByIdRef.current;
        const changed = apps.filter((a) => prevFp.get(a.id) !== applicationPersistFingerprint(a));
        if (changed.length === 0) return;
        const saved =
          changed.length === 1
            ? await saveApplicationById(changed[0].id, changed[0])
            : await saveApplicationsAll(apps);
        /** Same as after GET hydrate: skip one persist cycle so `root.applications` update does not re-queue PUT forever. */
        applicationsSkipNextPersistRef.current = true;
        lastApplicationFingerprintByIdRef.current = new Map(
          saved.map((a) => [a.id, applicationPersistFingerprint(a)]),
        );
        setRoot((prev) => finalizeDraft({ ...prev, applications: saved }));
      })
      .catch((err) => {
        showToast(
          err instanceof Error ? err.message : "Could not save applicants to the database.",
          "error",
        );
      });
  }, [showToast, can]);

  const refreshTimelineFromApi = useCallback(async () => {
    if (authLoading || !can("construction_timeline")) return;
    const rows = await fetchConstructionTimelinePhases();
    setRoot((prev) =>
      finalizeDraft({
        ...prev,
        constructionTimelinePhases: rows.map(phaseFromTimelineDto),
      }),
    );
  }, [authLoading, can]);

  const refreshStandardResourcesFromApi = useCallback(async () => {
    if (authLoading || !can("standard_resources")) return;
    const list = await fetchStandardResources();
    setRoot((prev) => finalizeDraft({ ...prev, standardResources: list }));
  }, [authLoading, can]);

  const refreshSubcontractorMastersFromApi = useCallback(async () => {
    if (authLoading || !can("subcontractor_masters")) return;
    const list = await fetchSubcontractorMasters();
    setRoot((prev) => finalizeDraft({ ...prev, subcontractorMasters: list }));
  }, [authLoading, can]);

  const refreshEngineerMastersFromApi = useCallback(async () => {
    if (authLoading || (!can("engineer_masters") && !canAccessApplicationsHub(can))) return;
    const list = await fetchEngineerMasters();
    setRoot((prev) => finalizeDraft({ ...prev, engineerMasters: list }));
  }, [authLoading, can]);

  const refreshAgentsFromApi = useCallback(async () => {
    if (authLoading || !can("agents")) return;
    const list = await fetchAgents();
    setRoot((prev) => finalizeDraft({ ...prev, agentMasters: list }));
  }, [authLoading, can]);

  const refreshGeoZonesFromApi = useCallback(async () => {
    if (authLoading || !can("geo_zones")) return;
    const list = await fetchGeoZones();
    setRoot((prev) => finalizeDraft({ ...prev, geoZones: list }));
  }, [authLoading, can]);

  const refreshStoreYardsFromApi = useCallback(async () => {
    if (authLoading || !canAnyInventory(can)) return;
    const list = await fetchStoreYards();
    setRoot((prev) => finalizeDraft({ ...prev, storeYards: list }));
  }, [authLoading, can]);

  const refreshMilestoneTemplatesFromApi = useCallback(async () => {
    if (authLoading || !can("milestone_templates")) return;
    const list = await fetchMilestoneTemplates();
    setRoot((prev) => finalizeDraft({ ...prev, milestoneTemplates: list }));
  }, [authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!can("construction_timeline")) {
      timelineApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, constructionTimelinePhases: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchConstructionTimelinePhases();
        if (cancelled) return;
        timelineApiActiveRef.current = true;
        setRoot((prev) =>
          finalizeDraft({
            ...prev,
            constructionTimelinePhases: rows.map(phaseFromTimelineDto),
          }),
        );
      } catch (e) {
        timelineApiActiveRef.current = false;
        showToast(
          e instanceof Error ? e.message : "Could not load construction timeline from the server.",
          "error",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!can("standard_resources")) {
      standardResourcesApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, standardResources: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchStandardResources();
        if (cancelled) return;
        standardResourcesApiActiveRef.current = true;
        setRoot((prev) => finalizeDraft({ ...prev, standardResources: list }));
      } catch (e) {
        standardResourcesApiActiveRef.current = false;
        showToast(
          e instanceof Error ? e.message : "Could not load standard resources from the server.",
          "error",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!can("subcontractor_masters")) {
      subcontractorMastersApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, subcontractorMasters: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchSubcontractorMasters();
        if (cancelled) return;
        subcontractorMastersApiActiveRef.current = true;
        setRoot((prev) => finalizeDraft({ ...prev, subcontractorMasters: list }));
      } catch (e) {
        subcontractorMastersApiActiveRef.current = false;
        showToast(
          e instanceof Error ? e.message : "Could not load subcontractor masters from the server.",
          "error",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    /** Keep roster in memory for assignment UX whenever user can open the applicant hub (not only Engineer onboarding). */
    if (!can("engineer_masters") && !canAccessApplicationsHub(can)) {
      engineerMastersApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, engineerMasters: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchEngineerMasters();
        if (cancelled) return;
        engineerMastersApiActiveRef.current = true;
        setRoot((prev) => finalizeDraft({ ...prev, engineerMasters: list }));
      } catch (e) {
        engineerMastersApiActiveRef.current = false;
        showToast(
          e instanceof Error ? e.message : "Could not load engineer masters from the server.",
          "error",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!can("agents")) {
      agentMastersApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, agentMasters: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchAgents();
        if (cancelled) return;
        agentMastersApiActiveRef.current = true;
        setRoot((prev) => finalizeDraft({ ...prev, agentMasters: list }));
      } catch (e) {
        agentMastersApiActiveRef.current = false;
        showToast(e instanceof Error ? e.message : "Could not load agents from the server.", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!can("geo_zones")) {
      geoZonesApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, geoZones: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchGeoZones();
        if (cancelled) return;
        geoZonesApiActiveRef.current = true;
        setRoot((prev) => finalizeDraft({ ...prev, geoZones: list }));
      } catch (e) {
        geoZonesApiActiveRef.current = false;
        showToast(e instanceof Error ? e.message : "Could not load geo zones from the server.", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!canAnyInventory(can)) {
      storeYardsApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, storeYards: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchStoreYards();
        if (cancelled) return;
        storeYardsApiActiveRef.current = can("inventory_stores_yards");
        const scoped = filterStoreYardsByConsoleScope(list, user, authRequired);
        setRoot((prev) => finalizeDraft({ ...prev, storeYards: scoped }));
      } catch (e) {
        storeYardsApiActiveRef.current = false;
        showToast(e instanceof Error ? e.message : "Could not load store yards from the server.", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can, user, authRequired]);

  useEffect(() => {
    if (authLoading) return;
    if (!can("milestone_templates")) {
      milestoneTemplatesApiActiveRef.current = false;
      setRoot((prev) => finalizeDraft({ ...prev, milestoneTemplates: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchMilestoneTemplates();
        if (cancelled) return;
        milestoneTemplatesApiActiveRef.current = true;
        setRoot((prev) => finalizeDraft({ ...prev, milestoneTemplates: list }));
      } catch (e) {
        milestoneTemplatesApiActiveRef.current = false;
        showToast(
          e instanceof Error ? e.message : "Could not load milestone templates from the server.",
          "error",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  useEffect(() => {
    if (authLoading) return;
    if (!canAccessApplicationsHub(can)) {
      applicationsApiActiveRef.current = false;
      applicationsHydratedRef.current = true;
      lastApplicationFingerprintByIdRef.current = new Map();
      setRoot((prev) => finalizeDraft({ ...prev, applications: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const apps = await fetchApplications();
        if (cancelled) return;
        applicationsApiActiveRef.current = true;
        applicationsSkipNextPersistRef.current = true;
        const nextApps = [...apps];
        setRoot((prev) => {
          const next = finalizeDraft({ ...prev, applications: nextApps });
          lastApplicationFingerprintByIdRef.current = new Map(
            next.applications.map((a) => [a.id, applicationPersistFingerprint(a)]),
          );
          return next;
        });
        applicationsHydratedRef.current = true;
      } catch (e) {
        applicationsApiActiveRef.current = false;
        applicationsHydratedRef.current = true;
        showToast(
          e instanceof Error
            ? e.message
            : "Could not load applicants from the server. They stay in the browser until MySQL is configured.",
          "error",
        );
        setRoot((prev) => finalizeDraft({ ...prev }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, authLoading, can]);

  const refreshApplicationsFromApi = useCallback(async () => {
    if (authLoading) return;
    if (!canAccessApplicationsHub(can)) return;
    if (!applicationsApiActiveRef.current) return;
    try {
      applicationsSkipNextPersistRef.current = true;
      const apps = await fetchApplications();
      setRoot((prev) => {
        const next = finalizeDraft({ ...prev, applications: [...apps] });
        lastApplicationFingerprintByIdRef.current = new Map(
          next.applications.map((a) => [a.id, applicationPersistFingerprint(a)]),
        );
        return next;
      });
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "Could not refresh applicants from the server.",
        "error",
      );
    }
  }, [authLoading, can, showToast]);

  /** Dossier edits use PATCH `/dossier`, `/validations`, `/ho-banking`, etc. — not a debounced full PUT. */

  const getById = useCallback(
    (id: string) => root.applications.find((a) => a.id === id),
    [root.applications]
  );

  const getSubcontractorMaster = useCallback(
    (id: string) => root.subcontractorMasters.find((m) => m.id === id),
    [root.subcontractorMasters]
  );

  const getEngineerMaster = useCallback(
    (id: string) => root.engineerMasters.find((m) => m.id === id),
    [root.engineerMasters],
  );

  const getAgentMaster = useCallback(
    (id: string) => root.agentMasters.find((m) => m.id === id),
    [root.agentMasters],
  );

  const getMilestoneTemplate = useCallback(
    (id: string) => root.milestoneTemplates.find((t) => t.id === id),
    [root.milestoneTemplates]
  );

  const getStandardResource = useCallback(
    (id: string) => root.standardResources.find((r) => r.id === id),
    [root.standardResources]
  );

  const patchRoot = useCallback((fn: (prev: AppRootState) => Partial<AppRootState>) => {
    setRoot((prev) => finalizeDraft({ ...prev, ...fn(prev) } as AppRootState));
  }, []);

  const updateHo = useCallback(
    (id: string, ho: Partial<HoFields>) => {
      if (!requireApplicantDossierMutation()) return;
      patchRoot((prev) => ({
        subcontractorMasters: prev.subcontractorMasters,
        applications: prev.applications.map((a) => (a.id !== id ? a : { ...a, ho: { ...a.ho, ...ho } })),
      }));
    },
    [patchRoot, requireApplicantDossierMutation],
  );

  const mergeApplicationFromServer = useCallback((id: string, updated: Application) => {
    applicationsSkipNextPersistRef.current = true;
    lastApplicationFingerprintByIdRef.current.set(id, applicationPersistFingerprint(updated));
    setRoot((prev) => {
      const applications = prev.applications.map((a) => (a.id !== id ? a : updated));
      return finalizeDraft({ ...prev, applications });
    });
  }, []);

  const partialApplicationToDossierPatch = (patch: Partial<Application>): ApplicationDossierPatch => {
    const out: ApplicationDossierPatch = {};
    if (Object.prototype.hasOwnProperty.call(patch, "district")) out.district = patch.district;
    if (Object.prototype.hasOwnProperty.call(patch, "mandal")) out.mandal = patch.mandal;
    if (Object.prototype.hasOwnProperty.call(patch, "village")) out.village = patch.village;
    if (Object.prototype.hasOwnProperty.call(patch, "status")) out.status = patch.status;
    if (Object.prototype.hasOwnProperty.call(patch, "receivedAt")) out.receivedAt = patch.receivedAt;
    if (Object.prototype.hasOwnProperty.call(patch, "leadId")) out.leadId = patch.leadId;
    if (Object.prototype.hasOwnProperty.call(patch, "holdReasons")) out.holdReasons = patch.holdReasons;
    if (patch.mobile) out.mobile = patch.mobile;
    if (patch.validations) out.validations = patch.validations as ValidationsPatch;
    if (patch.ho) out.ho = patch.ho;
    if (patch.subcontractor) out.subcontractor = patch.subcontractor;
    if (patch.engineer) out.engineer = patch.engineer;
    return out;
  };

  const persistDossierPatch = useCallback(
    async (id: string, patch: ApplicationDossierPatch) => {
      if (!requireApplicantDossierMutation()) return;
      const updated = await patchApplicationDossier(id, patch);
      mergeApplicationFromServer(id, updated);
      return updated;
    },
    [requireApplicantDossierMutation, mergeApplicationFromServer],
  );

  const updateApplication = useCallback(
    async (id: string, patch: Partial<Application>) => {
      const dossierPatch = partialApplicationToDossierPatch(patch);
      if (Object.keys(dossierPatch).length === 0) return;
      try {
        await persistDossierPatch(id, dossierPatch);
        showToast("Application updated.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not save application.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast],
  );

  const updateValidations = useCallback(
    async (id: string, partial: Partial<Validations>) => {
      if (!requireApplicantDossierMutation()) return;
      const patch: ValidationsPatch = {};
      if (Object.prototype.hasOwnProperty.call(partial, "nameMatchAcrossSources")) {
        patch.nameMatchAcrossSources = partial.nameMatchAcrossSources;
      }
      if (Object.prototype.hasOwnProperty.call(partial, "aadhaarVerified")) {
        patch.aadhaarVerified = partial.aadhaarVerified;
      }
      if (Object.prototype.hasOwnProperty.call(partial, "bankAccountVerified")) {
        patch.bankAccountVerified = partial.bankAccountVerified;
      }
      if (Object.prototype.hasOwnProperty.call(partial, "workmanCardAcceptable")) {
        patch.workmanCardAcceptable = partial.workmanCardAcceptable;
      }
      if (Object.prototype.hasOwnProperty.call(partial, "aadhaarSeedingConfirmed")) {
        patch.aadhaarSeedingConfirmed = partial.aadhaarSeedingConfirmed;
      }
      if (Object.keys(patch).length === 0) return;
      try {
        const updated = await patchApplicationValidations(id, patch);
        mergeApplicationFromServer(id, updated);
        showToast("Verification details saved.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not save verification details.", "error");
        throw err;
      }
    },
    [showToast, requireApplicantDossierMutation, mergeApplicationFromServer],
  );

  const setHoldReasons = useCallback(
    async (id: string, reasons: string[]) => {
      if (!requireApplicantDossierMutation()) return;
      try {
        const updated = await patchApplicationValidations(id, { holdReasons: reasons });
        mergeApplicationFromServer(id, updated);
        showToast("Hold reasons updated.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not update hold reasons.", "error");
        throw err;
      }
    },
    [showToast, requireApplicantDossierMutation, mergeApplicationFromServer],
  );

  const confirmSeeding = useCallback(
    async (id: string) => {
      if (!requireApplicantDossierMutation()) return;
      try {
        const updated = await patchApplicationValidations(id, { aadhaarSeedingConfirmed: true });
        mergeApplicationFromServer(id, updated);
        showToast("Aadhaar seeding confirmed.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not confirm Aadhaar seeding.", "error");
        throw err;
      }
    },
    [showToast, requireApplicantDossierMutation, mergeApplicationFromServer],
  );

  const saveIciciDetails = useCallback(
    async (id: string, patch: HoBankingPatch) => {
      if (!requireApplicantDossierMutation()) return;
      try {
        const updated = await patchApplicationHoBanking(id, patch);
        mergeApplicationFromServer(id, updated);
        showToast("Banking details saved.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not save banking details.", "error");
        throw err;
      }
    },
    [showToast, requireApplicantDossierMutation, mergeApplicationFromServer],
  );

  const addWorkDoneBill = useCallback(
    async (id: string, fileName: string) => {
      if (!requireApplicantDossierMutation()) return;
      const billId = `bill-${Date.now()}`;
      try {
        await persistDossierPatch(id, { workDoneBillAppend: { id: billId, fileName } });
        showToast("Work-done bill added.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not add work-done bill.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const confirmBillSeeding = useCallback(
    async (id: string, billId: string) => {
      if (!requireApplicantDossierMutation()) return;
      try {
        await persistDossierPatch(id, { workDoneBillConfirmSeeding: { billId } });
        showToast("Seeding re-verified for this bill.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not confirm bill seeding.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const updateSubcontractor = useCallback(
    async (id: string, partial: Partial<SubcontractorLinkage>) => {
      if (!requireApplicantDossierMutation()) return;
      const app = rootRef.current.applications.find((a) => a.id === id);
      if (!app) return;
      let sub = { ...app.subcontractor, ...partial };
      const master = findSubcontractorMaster(rootRef.current.subcontractorMasters, sub.subcontractorMasterId);
      if (!isSubcontractorLinkageComplete(sub, master)) {
        sub = { ...sub, linkedAt: undefined };
      }
      const patch: ApplicationDossierPatch["subcontractor"] = {};
      if (Object.prototype.hasOwnProperty.call(partial, "subcontractorMasterId")) {
        patch.subcontractorMasterId = sub.subcontractorMasterId;
      }
      if (Object.prototype.hasOwnProperty.call(partial, "workOrderFileName")) {
        patch.workOrderFileName = sub.workOrderFileName;
      }
      if (!isSubcontractorLinkageComplete(sub, master)) {
        patch.linkedAt = undefined;
      } else if (Object.prototype.hasOwnProperty.call(partial, "linkedAt")) {
        patch.linkedAt = sub.linkedAt;
      }
      if (Object.keys(patch).length === 0) return;
      try {
        await persistDossierPatch(id, { subcontractor: patch });
        showToast("Subcontractor selection updated.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not update subcontractor.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const finalizeSubcontractor = useCallback(
    async (
      id: string,
      filePayload?: { workOrderFileBase64: string; workOrderMimeType: string | null },
    ) => {
      if (!requireApplicantDossierMutation()) return false;
      const app = rootRef.current.applications.find((a) => a.id === id);
      if (!app) return false;
      let sub = { ...app.subcontractor };
      if (filePayload?.workOrderFileBase64) {
        sub = {
          ...sub,
          workOrderFileBase64: filePayload.workOrderFileBase64,
          workOrderMimeType: filePayload.workOrderMimeType,
        };
      }
      const master = findSubcontractorMaster(rootRef.current.subcontractorMasters, sub.subcontractorMasterId);
      if (!isSubcontractorLinkageComplete(sub, master)) {
        showToast(
          "Choose an onboarded firm with full KYC (Aadhaar, bank details, cancelled cheque, Aadhaar doc, Udyam/MSME, labour licence) before finalising linkage.",
          "error",
        );
        return false;
      }
      const linkedAt = sub.linkedAt ?? new Date().toISOString();
      try {
        await persistDossierPatch(id, {
          subcontractor: {
            subcontractorMasterId: sub.subcontractorMasterId,
            workOrderFileName: sub.workOrderFileName,
            linkedAt,
            ...(filePayload?.workOrderFileBase64
              ? {
                  workOrderFileBase64: filePayload.workOrderFileBase64,
                  workOrderMimeType: filePayload.workOrderMimeType,
                }
              : {}),
          },
        });
        showToast("Subcontractor linkage finalised.", "success");
        return true;
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not finalise subcontractor linkage.", "error");
        return false;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const updateEngineer = useCallback(
    async (id: string, partial: Partial<EngineerLinkage>) => {
      if (!requireApplicantDossierMutation()) return;
      const app = rootRef.current.applications.find((a) => a.id === id);
      if (!app) return;
      const base = app.engineer ?? emptyEngineerLinkage();
      let eng = { ...base, ...partial };
      const master = findEngineerMaster(rootRef.current.engineerMasters, eng.engineerMasterId);
      if (!isEngineerLinkageComplete(eng, master)) {
        eng = { ...eng, linkedAt: undefined };
      }
      const patch: ApplicationDossierPatch["engineer"] = {};
      if (Object.prototype.hasOwnProperty.call(partial, "engineerMasterId")) {
        patch.engineerMasterId = eng.engineerMasterId;
      }
      if (!isEngineerLinkageComplete(eng, master)) {
        patch.linkedAt = undefined;
      } else if (Object.prototype.hasOwnProperty.call(partial, "linkedAt")) {
        patch.linkedAt = eng.linkedAt;
      }
      if (Object.keys(patch).length === 0) return;
      try {
        await persistDossierPatch(id, { engineer: patch });
        showToast("Engineer selection updated.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not update engineer.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const finalizeEngineer = useCallback(
    async (id: string) => {
      if (!requireApplicantDossierMutation()) return false;
      const app = rootRef.current.applications.find((a) => a.id === id);
      if (!app) return false;
      const eng = { ...(app.engineer ?? emptyEngineerLinkage()) };
      const master = findEngineerMaster(rootRef.current.engineerMasters, eng.engineerMasterId);
      if (!isEngineerLinkageComplete(eng, master)) {
        showToast(
          "Choose an engineer from the master list (name and phone required) before recording assignment.",
          "error",
        );
        return false;
      }
      const linkedAt = eng.linkedAt ?? new Date().toISOString();
      try {
        await persistDossierPatch(id, {
          engineer: { engineerMasterId: eng.engineerMasterId, linkedAt },
        });
        showToast("Engineer assignment recorded.", "success");
        return true;
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not record engineer assignment.", "error");
        return false;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const addSubcontractorMaster = useCallback(
    async (row: Omit<SubcontractorMaster, "id" | "createdAt">) => {
      if (subcontractorMastersApiActiveRef.current) {
        try {
          const created = await createSubcontractorMasterApi(row);
          await refreshSubcontractorMastersFromApi();
          showToast("Firm registered in master list.", "success");
          return created.id;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not register firm.", "error");
          return "";
        }
      }
      const newId = `subm-${Date.now()}`;
      const {
        cancelChequeBase64: _ccb,
        cancelChequeMimeType: _ccm,
        aadharDocBase64: _adb,
        aadharDocMimeType: _adm,
        gstCertBase64: _gcb,
        gstCertMimeType: _gcm,
        udyamMsmeBase64: _um,
        udyamMsmeMimeType: _umm,
        labourLicenseBase64: _ll,
        labourLicenseMimeType: _llm,
        ...restRow
      } = row;
      const created: SubcontractorMaster = {
        ...restRow,
        cancelChequeStored: Boolean(_ccb?.trim()) || Boolean(row.cancelChequeStored),
        aadharDocStored: Boolean(_adb?.trim()) || Boolean(row.aadharDocStored),
        gstCertStored: Boolean(_gcb?.trim()) || Boolean(row.gstCertStored),
        udyamMsmeStored: Boolean(_um?.trim()) || Boolean(row.udyamMsmeStored),
        labourLicenseStored: Boolean(_ll?.trim()) || Boolean(row.labourLicenseStored),
        id: newId,
        createdAt: new Date().toISOString(),
      };
      patchRoot((prev) => ({
        applications: prev.applications,
        subcontractorMasters: [...prev.subcontractorMasters, created],
      }));
      showToast("Firm registered in master list.", "success");
      return newId;
    },
    [patchRoot, refreshSubcontractorMastersFromApi, showToast],
  );

  const updateSubcontractorMaster = useCallback(
    async (id: string, patch: Partial<Omit<SubcontractorMaster, "id" | "createdAt">>) => {
      if (subcontractorMastersApiActiveRef.current) {
        try {
          await updateSubcontractorMasterApi(id, patch);
          await refreshSubcontractorMastersFromApi();
          showToast("Firm details updated.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not update firm.", "error");
          return false;
        }
      }
      patchRoot((prev) => {
        const {
          cancelChequeBase64: _ccb,
          cancelChequeMimeType: _ccm,
          aadharDocBase64: _adb,
          aadharDocMimeType: _adm,
          gstCertBase64: _gcb,
          gstCertMimeType: _gcm,
          udyamMsmeBase64: _um,
          udyamMsmeMimeType: _umm,
          labourLicenseBase64: _ll,
          labourLicenseMimeType: _llm,
          ...patchRest
        } = patch;
        const mergedPatch = { ...patchRest };
        if (typeof _ccb === "string" && _ccb.trim()) mergedPatch.cancelChequeStored = true;
        if (typeof _adb === "string" && _adb.trim()) mergedPatch.aadharDocStored = true;
        if (typeof _gcb === "string" && _gcb.trim()) mergedPatch.gstCertStored = true;
        if (typeof _um === "string" && _um.trim()) mergedPatch.udyamMsmeStored = true;
        if (typeof _ll === "string" && _ll.trim()) mergedPatch.labourLicenseStored = true;
        const nextMasters = prev.subcontractorMasters.map((m) => (m.id === id ? { ...m, ...mergedPatch } : m));
        const updatedMaster = nextMasters.find((m) => m.id === id);
        const applications = prev.applications.map((a) => {
          if (a.subcontractor.subcontractorMasterId !== id) return a;
          let sub = a.subcontractor;
          if (!isSubcontractorLinkageComplete(sub, updatedMaster)) {
            sub = { ...sub, linkedAt: undefined };
          }
          return { ...a, subcontractor: sub };
        });
        return { subcontractorMasters: nextMasters, engineerMasters: prev.engineerMasters, applications };
      });
      showToast("Firm details updated.", "success");
      return true;
    },
    [patchRoot, refreshSubcontractorMastersFromApi, showToast],
  );

  const deleteSubcontractorMaster = useCallback(
    async (id: string) => {
      if (subcontractorMastersApiActiveRef.current) {
        try {
          await deleteSubcontractorMasterApi(id);
          const list = await fetchSubcontractorMasters();
          if (applicationsApiActiveRef.current && canAccessApplicationsHub(can)) {
            try {
              applicationsSkipNextPersistRef.current = true;
              const apps = await fetchApplications();
              setRoot((prev) => finalizeDraft({ ...prev, subcontractorMasters: list, applications: apps }));
            } catch (e) {
              setRoot((prev) => finalizeDraft({ ...prev, subcontractorMasters: list }));
              showToast(
                e instanceof Error ? e.message : "Applicants list could not be refreshed; reload the page if needed.",
                "error",
              );
              return true;
            }
          } else {
            setRoot((prev) => finalizeDraft({ ...prev, subcontractorMasters: list }));
          }
          showToast("Firm removed from master list.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not delete firm.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({
        subcontractorMasters: prev.subcontractorMasters.filter((m) => m.id !== id),
        engineerMasters: prev.engineerMasters,
        applications: prev.applications.map((a) =>
          a.subcontractor.subcontractorMasterId === id
            ? {
                ...a,
                subcontractor: {
                  ...a.subcontractor,
                  subcontractorMasterId: null,
                  linkedAt: undefined,
                },
              }
            : a,
        ),
      }));
      showToast("Firm removed from master list.", "success");
      return true;
    },
    [patchRoot, showToast, can],
  );

  const addEngineerMaster = useCallback(
    async (row: Omit<EngineerMaster, "id" | "createdAt">) => {
      if (engineerMastersApiActiveRef.current) {
        try {
          const created = await createEngineerMasterApi(row);
          await refreshEngineerMastersFromApi();
          showToast("Engineer registered in master list.", "success");
          return created.id;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not register engineer.", "error");
          return "";
        }
      }
      const newId = `eng-${Date.now()}`;
      const created: EngineerMaster = {
        fullName: row.fullName.trim(),
        phone: row.phone.trim(),
        email: row.email.trim(),
        employeeCode: row.employeeCode.trim(),
        notes: row.notes.trim(),
        id: newId,
        createdAt: new Date().toISOString(),
      };
      patchRoot((prev) => ({
        engineerMasters: [...prev.engineerMasters, created],
      }));
      showToast("Engineer registered in master list.", "success");
      return newId;
    },
    [patchRoot, refreshEngineerMastersFromApi, showToast],
  );

  const updateEngineerMaster = useCallback(
    async (id: string, patch: Partial<Omit<EngineerMaster, "id" | "createdAt">>) => {
      if (engineerMastersApiActiveRef.current) {
        try {
          await updateEngineerMasterApi(id, patch);
          await refreshEngineerMastersFromApi();
          showToast("Engineer details updated.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not update engineer.", "error");
          return false;
        }
      }
      patchRoot((prev) => {
        const nextMasters = prev.engineerMasters.map((m) => (m.id === id ? { ...m, ...patch } : m));
        const updatedMaster = nextMasters.find((m) => m.id === id);
        const applications = prev.applications.map((a) => {
          if (a.engineer?.engineerMasterId !== id) return a;
          const eng = a.engineer ?? emptyEngineerLinkage();
          if (!isEngineerLinkageComplete(eng, updatedMaster)) {
            return { ...a, engineer: { ...eng, linkedAt: undefined } };
          }
          return a;
        });
        return { engineerMasters: nextMasters, applications };
      });
      showToast("Engineer details updated.", "success");
      return true;
    },
    [patchRoot, refreshEngineerMastersFromApi, showToast],
  );

  const deleteEngineerMaster = useCallback(
    async (id: string) => {
      if (engineerMastersApiActiveRef.current) {
        try {
          await deleteEngineerMasterApi(id);
          const list = await fetchEngineerMasters();
          if (applicationsApiActiveRef.current && canAccessApplicationsHub(can)) {
            try {
              applicationsSkipNextPersistRef.current = true;
              const apps = await fetchApplications();
              setRoot((prev) => finalizeDraft({ ...prev, engineerMasters: list, applications: apps }));
            } catch (e) {
              setRoot((prev) => finalizeDraft({ ...prev, engineerMasters: list }));
              showToast(
                e instanceof Error ? e.message : "Applicants list could not be refreshed; reload the page if needed.",
                "error",
              );
              return true;
            }
          } else {
            setRoot((prev) => finalizeDraft({ ...prev, engineerMasters: list }));
          }
          showToast("Engineer removed from master list.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not delete engineer.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({
        engineerMasters: prev.engineerMasters.filter((m) => m.id !== id),
        applications: prev.applications.map((a) =>
          a.engineer?.engineerMasterId === id
            ? { ...a, engineer: { engineerMasterId: null, linkedAt: undefined } }
            : a,
        ),
      }));
      showToast("Engineer removed from master list.", "success");
      return true;
    },
    [patchRoot, showToast, can],
  );

  const addAgentMaster = useCallback(
    async (row: AgentMasterUpsertPayload) => {
      if (agentMastersApiActiveRef.current) {
        try {
          const created = await createAgentApi(row);
          await refreshAgentsFromApi();
          showToast("Agent saved.", "success");
          return created.id;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not save agent.", "error");
          return "";
        }
      }
      const newId = `agent-${Date.now()}`;
      const { newPassword: _np, clearPassword: _cp, ...profile } = row;
      const created: AgentMaster = {
        ...profile,
        id: newId,
        hasPassword: Boolean(row.newPassword?.trim()),
        passwordMode: row.passwordMode ?? "random",
        onboardingEmailSentAt: null,
        createdAt: new Date().toISOString(),
      };
      patchRoot((prev) => ({
        applications: prev.applications,
        subcontractorMasters: prev.subcontractorMasters,
        agentMasters: [...prev.agentMasters, created],
      }));
      showToast("Agent saved.", "success");
      return newId;
    },
    [patchRoot, refreshAgentsFromApi, showToast],
  );

  const updateAgentMaster = useCallback(
    async (id: string, patch: Partial<AgentMasterUpsertPayload>) => {
      if (agentMastersApiActiveRef.current) {
        try {
          await updateAgentApi(id, patch);
          await refreshAgentsFromApi();
          showToast("Agent updated.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not update agent.", "error");
          return false;
        }
      }
      const { newPassword, clearPassword, ...profile } = patch;
      patchRoot((prev) => ({
        applications: prev.applications,
        subcontractorMasters: prev.subcontractorMasters,
        agentMasters: prev.agentMasters.map((m) => {
          if (m.id !== id) return m;
          let hasPassword = m.hasPassword;
          if (clearPassword === true && (newPassword === undefined || String(newPassword).trim() === "")) {
            hasPassword = false;
          } else if (newPassword !== undefined && String(newPassword).trim() !== "") {
            hasPassword = true;
          }
          if (profile.passwordMode === "random" && m.passwordMode === "custom") {
            hasPassword = false;
          }
          return { ...m, ...profile, hasPassword };
        }),
      }));
      showToast("Agent updated.", "success");
      return true;
    },
    [patchRoot, refreshAgentsFromApi, showToast],
  );

  const deleteAgentMaster = useCallback(
    async (id: string) => {
      if (agentMastersApiActiveRef.current) {
        try {
          await deleteAgentApi(id);
          await refreshAgentsFromApi();
          showToast("Agent removed.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not delete agent.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({
        applications: prev.applications,
        subcontractorMasters: prev.subcontractorMasters,
        agentMasters: prev.agentMasters.filter((m) => m.id !== id),
      }));
      showToast("Agent removed.", "success");
      return true;
    },
    [patchRoot, refreshAgentsFromApi, showToast],
  );

  const sendAgentOnboardingEmail = useCallback(
    async (id: string, options?: { customPasswordForEmail?: string }) => {
      if (!agentMastersApiActiveRef.current) {
        showToast("Connect to the server to send onboarding email.", "error");
        return false;
      }
      try {
        await sendAgentOnboardingEmailApi(id, options);
        await refreshAgentsFromApi();
        showToast("Onboarding email sent.", "success");
        return true;
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not send onboarding email.", "error");
        return false;
      }
    },
    [refreshAgentsFromApi, showToast],
  );

  const addMilestoneTemplate = useCallback(
    async (input: {
      title: string;
      notes?: string;
      items: { kind: MilestoneItemKind; label: string }[];
      linkedConstructionTimelinePhaseIds?: string[];
      linkedResourceIds?: string[];
    }) => {
      const title = input.title.trim();
      if (!title) {
        showToast("Enter a milestone title.", "error");
        return false;
      }
      if (milestoneTemplatesApiActiveRef.current) {
        try {
          const prev = rootRef.current;
          const linkedConstructionTimelinePhaseIds = normalizeLinkedTimelinePhaseIds(
            prev.constructionTimelinePhases,
            input.linkedConstructionTimelinePhaseIds,
          );
          const linkedResourceIds = normalizeLinkedResourceIds(prev.standardResources, input.linkedResourceIds);
          const items = input.items
            .map((it) => ({ kind: it.kind, label: it.label.trim() }))
            .filter((it) => it.label);
          await createMilestoneTemplateApi({
            title,
            notes: (input.notes ?? "").trim(),
            items,
            linkedConstructionTimelinePhaseIds,
            linkedResourceIds,
          });
          await refreshMilestoneTemplatesFromApi();
          showToast("Milestone template created.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not create milestone template.", "error");
          return false;
        }
      }
      patchRoot((prev) => {
        const sortOrder = prev.milestoneTemplates.reduce((m, t) => Math.max(m, t.sortOrder), 0) + 1;
        const ts = Date.now();
        const items = input.items
          .map((it) => ({
            id: `mti-${ts}-${Math.random().toString(36).slice(2, 9)}`,
            kind: it.kind,
            label: it.label.trim(),
          }))
          .filter((it) => it.label);
        const linkedConstructionTimelinePhaseIds = normalizeLinkedTimelinePhaseIds(
          prev.constructionTimelinePhases,
          input.linkedConstructionTimelinePhaseIds,
        );
        const linkedResourceIds = normalizeLinkedResourceIds(prev.standardResources, input.linkedResourceIds);
        const t: MilestoneTemplate = {
          id: `mt-${ts}`,
          title,
          notes: (input.notes ?? "").trim(),
          sortOrder,
          createdAt: new Date().toISOString(),
          items,
          linkedConstructionTimelinePhaseIds,
          linkedResourceIds,
        };
        return { milestoneTemplates: [...prev.milestoneTemplates, t] };
      });
      showToast("Milestone template created.", "success");
      return true;
    },
    [patchRoot, refreshMilestoneTemplatesFromApi, showToast],
  );

  const updateMilestoneTemplate = useCallback(
    async (
      id: string,
      patch: Partial<
        Pick<
          MilestoneTemplate,
          "title" | "notes" | "sortOrder" | "linkedConstructionTimelinePhaseIds" | "linkedResourceIds"
        >
      >,
    ) => {
      if (milestoneTemplatesApiActiveRef.current) {
        try {
          const prev = rootRef.current;
          const curTpl = prev.milestoneTemplates.find((x) => x.id === id);
          if (!curTpl) {
            showToast("Milestone template not found.", "error");
            return;
          }
          const body: Partial<
            Pick<
              MilestoneTemplate,
              "title" | "notes" | "sortOrder" | "linkedConstructionTimelinePhaseIds" | "linkedResourceIds"
            >
          > = {};
          if (patch.title !== undefined) body.title = patch.title.trim() || curTpl.title;
          if (patch.notes !== undefined) body.notes = patch.notes.trim();
          if (patch.sortOrder !== undefined) body.sortOrder = patch.sortOrder;
          if (patch.linkedConstructionTimelinePhaseIds !== undefined) {
            body.linkedConstructionTimelinePhaseIds = normalizeLinkedTimelinePhaseIds(
              prev.constructionTimelinePhases,
              patch.linkedConstructionTimelinePhaseIds,
            );
          }
          if (patch.linkedResourceIds !== undefined) {
            body.linkedResourceIds = normalizeLinkedResourceIds(
              prev.standardResources,
              patch.linkedResourceIds,
            );
          }
          if (Object.keys(body).length === 0) return;
          await updateMilestoneTemplateApi(id, body);
          await refreshMilestoneTemplatesFromApi();
          showToast("Milestone master saved.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not save milestone template.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        milestoneTemplates: prev.milestoneTemplates.map((t) => {
          if (t.id !== id) return t;
          const nextTitle =
            patch.title !== undefined ? patch.title.trim() || t.title : t.title;
          const nextNotes = patch.notes !== undefined ? patch.notes.trim() : t.notes;
          let linkedConstructionTimelinePhaseIds = t.linkedConstructionTimelinePhaseIds;
          if (patch.linkedConstructionTimelinePhaseIds !== undefined) {
            linkedConstructionTimelinePhaseIds = normalizeLinkedTimelinePhaseIds(
              prev.constructionTimelinePhases,
              patch.linkedConstructionTimelinePhaseIds,
            );
          }
          let linkedResourceIds = t.linkedResourceIds;
          if (patch.linkedResourceIds !== undefined) {
            linkedResourceIds = normalizeLinkedResourceIds(prev.standardResources, patch.linkedResourceIds);
          }
          return {
            ...t,
            ...patch,
            title: nextTitle,
            notes: nextNotes,
            linkedConstructionTimelinePhaseIds,
            linkedResourceIds,
          };
        }),
      }));
      showToast("Milestone master saved.", "success");
    },
    [patchRoot, refreshMilestoneTemplatesFromApi, showToast],
  );

  const removeMilestoneTemplate = useCallback(
    async (id: string): Promise<boolean> => {
      if (milestoneTemplatesApiActiveRef.current) {
        try {
          await deleteMilestoneTemplateApi(id);
          await refreshMilestoneTemplatesFromApi();
          showToast("Milestone template removed.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove milestone template.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({
        milestoneTemplates: prev.milestoneTemplates.filter((t) => t.id !== id),
      }));
      showToast("Milestone template removed.", "success");
      return true;
    },
    [patchRoot, refreshMilestoneTemplatesFromApi, showToast],
  );

  const addMilestoneTemplateItem = useCallback(
    async (templateId: string, item: { kind: MilestoneItemKind; label: string }) => {
      const label = item.label.trim();
      if (!label) {
        showToast("Enter an item label.", "error");
        return false;
      }
      if (milestoneTemplatesApiActiveRef.current) {
        try {
          await createMilestoneTemplateItemApi(templateId, { kind: item.kind, label });
          await refreshMilestoneTemplatesFromApi();
          showToast("Checklist item added.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not add checklist item.", "error");
          return false;
        }
      }
      let added = false;
      patchRoot((prev) => {
        if (!prev.milestoneTemplates.some((t) => t.id === templateId)) return {};
        added = true;
        const newItem = {
          id: `mti-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          kind: item.kind,
          label,
        };
        return {
          milestoneTemplates: prev.milestoneTemplates.map((t) =>
            t.id === templateId ? { ...t, items: [...t.items, newItem] } : t
          ),
        };
      });
      if (added) showToast("Checklist item added.", "success");
      else showToast("Milestone template not found.", "error");
      return added;
    },
    [patchRoot, refreshMilestoneTemplatesFromApi, showToast],
  );

  const removeMilestoneTemplateItem = useCallback(
    async (templateId: string, itemId: string) => {
      if (milestoneTemplatesApiActiveRef.current) {
        try {
          await deleteMilestoneTemplateItemApi(templateId, itemId);
          await refreshMilestoneTemplatesFromApi();
          showToast("Checklist item removed.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove checklist item.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        milestoneTemplates: prev.milestoneTemplates.map((t) =>
          t.id === templateId ? { ...t, items: t.items.filter((i) => i.id !== itemId) } : t
        ),
      }));
      showToast("Checklist item removed.", "success");
    },
    [patchRoot, refreshMilestoneTemplatesFromApi, showToast],
  );

  const setMilestonePhaseComplete = useCallback(
    (applicationId: string, templateId: string, done: boolean) => {
      if (!requireApplicantDossierMutation()) return;
      const at = new Date().toISOString();
      // Optimistic local update
      patchRoot((prev) => ({
        applications: prev.applications.map((a) => {
          if (a.id !== applicationId) return a;
          return {
            ...a,
            milestoneProgress: a.milestoneProgress.map((p) =>
              p.templateId !== templateId
                ? p
                : { ...p, milestoneCompletedAt: done ? (p.milestoneCompletedAt ?? at) : null }
            ),
          };
        }),
      }));
      // Persist to server — use existing completedAt if already set, otherwise the new timestamp
      const existingCompletedAt = rootRef.current.applications
        .find((a) => a.id === applicationId)
        ?.milestoneProgress.find((p) => p.templateId === templateId)
        ?.milestoneCompletedAt ?? null;
      const completedAt = done ? (existingCompletedAt ?? at) : null;
      void saveMilestoneComplete(applicationId, templateId, done, completedAt).catch((err) => {
        showToast(err instanceof Error ? err.message : "Could not save milestone status.", "error");
      });
      showToast(done ? "Milestone marked complete." : "Milestone completion cleared.", "success");
    },
    [patchRoot, showToast, requireApplicantDossierMutation],
  );

  const setMilestoneItemBillLink = useCallback(
    async (
      applicationId: string,
      templateId: string,
      templateItemId: string,
      workDoneBillId: string | null,
    ) => {
      if (!requireApplicantDossierMutation()) return;
      const app = rootRef.current.applications.find((a) => a.id === applicationId);
      if (!app) {
        showToast("Application not found.", "error");
        return;
      }
      const bid = workDoneBillId?.trim() || null;
      if (bid && !app.workDoneBills.some((b) => b.id === bid)) {
        showToast("That bill is not on this file. Add the bill on the Bills tab first.", "error");
        return;
      }
      try {
        await persistDossierPatch(applicationId, {
          milestoneItem: { templateId, templateItemId, workDoneBillId: bid },
        });
        showToast("Bill link updated.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not update bill link.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const setMilestoneItemDone = useCallback(
    async (
      applicationId: string,
      templateId: string,
      templateItemId: string,
      done: boolean,
      workDoneBillId?: string | null,
    ) => {
      if (!requireApplicantDossierMutation()) return;
      const at = new Date().toISOString();
      const tpl = rootRef.current.milestoneTemplates.find((t) => t.id === templateId);
      const itemDef = tpl?.items.find((i) => i.id === templateItemId);
      if (!itemDef) {
        showToast("Milestone item not found.", "error");
        return;
      }
      const app = rootRef.current.applications.find((a) => a.id === applicationId);
      if (!app) {
        showToast("Application not found.", "error");
        return;
      }
      const progress = app.milestoneProgress.find((p) => p.templateId === templateId);
      const itemProgress = progress?.itemProgress.find((ip) => ip.templateItemId === templateItemId);
      let billId: string | null = null;
      if (done && itemDef.kind === "work_done_bill") {
        billId = (workDoneBillId ?? itemProgress?.workDoneBillId)?.trim() || null;
        if (!billId || !app.workDoneBills.some((b) => b.id === billId)) {
          showToast("Choose a valid work-done bill for this item.", "error");
          return;
        }
      }
      const completedAt = done ? (itemProgress?.completedAt ?? at) : null;
      try {
        await persistDossierPatch(applicationId, {
          milestoneItem: {
            templateId,
            templateItemId,
            completedAt,
            ...(done && itemDef.kind === "work_done_bill" ? { workDoneBillId: billId } : {}),
            ...(!done && itemDef.kind === "work_done_bill" ? { workDoneBillId: null } : {}),
          },
        });
        showToast(done ? "Checklist item marked done." : "Checklist item cleared.", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Could not update checklist item.", "error");
        throw err;
      }
    },
    [persistDossierPatch, showToast, requireApplicantDossierMutation],
  );

  const addGeoZone = useCallback(
    async (input: { name: string; notes?: string }) => {
      const name = input.name.trim();
      if (!name) {
        showToast("Enter a zone name.", "error");
        return "";
      }
      if (geoZonesApiActiveRef.current) {
        try {
          const row = await createGeoZoneApi({ name, notes: (input.notes ?? "").trim() });
          await refreshGeoZonesFromApi();
          showToast("Geographic zone created.", "success");
          return row.id;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not create zone.", "error");
          return "";
        }
      }
      const id = `zone-${Date.now()}`;
      patchRoot((prev) => {
        const sortOrder = prev.geoZones.reduce((m, z) => Math.max(m, z.sortOrder), 0) + 1;
        const z: GeoZone = {
          id,
          name,
          notes: (input.notes ?? "").trim(),
          districtKeys: [],
          mandalRefs: [],
          villageRefs: [],
          sortOrder,
          createdAt: new Date().toISOString(),
        };
        return { geoZones: [...prev.geoZones, z] };
      });
      showToast("Geographic zone created.", "success");
      return id;
    },
    [patchRoot, refreshGeoZonesFromApi, showToast],
  );

  const updateGeoZone = useCallback(
    async (
      id: string,
      patch: Partial<
        Pick<GeoZone, "name" | "notes" | "districtKeys" | "mandalRefs" | "villageRefs" | "sortOrder">
      >,
    ) => {
      if (geoZonesApiActiveRef.current) {
        try {
          const current = rootRef.current.geoZones.find((z) => z.id === id);
          if (!current) {
            showToast("Zone not found.", "error");
            return;
          }
          const body: Partial<
            Pick<GeoZone, "name" | "notes" | "districtKeys" | "mandalRefs" | "villageRefs" | "sortOrder">
          > = {};
          if (patch.name !== undefined) body.name = patch.name.trim() || current.name;
          if (patch.notes !== undefined) body.notes = patch.notes.trim();
          if (patch.districtKeys !== undefined) body.districtKeys = patch.districtKeys;
          if (patch.mandalRefs !== undefined) body.mandalRefs = patch.mandalRefs;
          if (patch.villageRefs !== undefined) body.villageRefs = patch.villageRefs;
          if (patch.sortOrder !== undefined) body.sortOrder = patch.sortOrder;
          if (Object.keys(body).length === 0) return;
          await updateGeoZoneApi(id, body);
          await refreshGeoZonesFromApi();
          showToast("Zone updated.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not update zone.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        geoZones: prev.geoZones.map((z) => {
          if (z.id !== id) return z;
          return {
            ...z,
            ...patch,
            name: patch.name !== undefined ? patch.name.trim() || z.name : z.name,
            notes: patch.notes !== undefined ? patch.notes.trim() : z.notes,
            districtKeys: patch.districtKeys ?? z.districtKeys,
            mandalRefs: patch.mandalRefs ?? z.mandalRefs,
            villageRefs: patch.villageRefs ?? z.villageRefs,
          };
        }),
      }));
      showToast("Zone updated.", "success");
    },
    [patchRoot, refreshGeoZonesFromApi, showToast],
  );

  const removeGeoZone = useCallback(
    async (id: string) => {
      if (geoZonesApiActiveRef.current) {
        try {
          await deleteGeoZoneApi(id);
          await refreshGeoZonesFromApi();
          showToast("Zone removed.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove zone.", "error");
        }
        return;
      }
      patchRoot((prev) => ({ geoZones: prev.geoZones.filter((z) => z.id !== id) }));
      showToast("Zone removed.", "success");
    },
    [patchRoot, refreshGeoZonesFromApi, showToast],
  );

  const addStoreYard = useCallback(
    async (input: { name: string; notes?: string; address?: string }) => {
      const name = input.name.trim();
      if (!name) {
        showToast("Enter a store or yard name.", "error");
        return "";
      }
      if (storeYardsApiActiveRef.current) {
        try {
          const row = await createStoreYardApi({
            name,
            notes: (input.notes ?? "").trim(),
            address: (input.address ?? "").trim(),
          });
          await refreshStoreYardsFromApi();
          showToast("Store / yard created.", "success");
          return row.id;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not create store yard.", "error");
          return "";
        }
      }
      const id = `store-${Date.now()}`;
      patchRoot((prev) => {
        const sortOrder = prev.storeYards.reduce((m, s) => Math.max(m, s.sortOrder), 0) + 1;
        const s: StoreYard = {
          id,
          name,
          notes: (input.notes ?? "").trim(),
          address: (input.address ?? "").trim(),
          scopeType: "district",
          districtKeys: [],
          mandalRefs: [],
          villageRefs: [],
          zoneIds: [],
          sortOrder,
          createdAt: new Date().toISOString(),
        };
        return { storeYards: [...prev.storeYards, s] };
      });
      showToast("Store / yard created.", "success");
      return id;
    },
    [patchRoot, refreshStoreYardsFromApi, showToast],
  );

  const updateStoreYard = useCallback(
    async (
      id: string,
      patch: Partial<
        Pick<
          StoreYard,
          | "name"
          | "notes"
          | "address"
          | "scopeType"
          | "districtKeys"
          | "mandalRefs"
          | "villageRefs"
          | "zoneIds"
          | "sortOrder"
        >
      >,
    ) => {
      if (storeYardsApiActiveRef.current) {
        try {
          const current = rootRef.current.storeYards.find((s) => s.id === id);
          const body: Partial<
            Pick<
              StoreYard,
              | "name"
              | "notes"
              | "address"
              | "scopeType"
              | "districtKeys"
              | "mandalRefs"
              | "villageRefs"
              | "zoneIds"
              | "sortOrder"
            >
          > = {};
          if (patch.name !== undefined) {
            const nextName = patch.name.trim() || current?.name;
            if (!nextName) {
              showToast("Name cannot be empty.", "error");
              return;
            }
            body.name = nextName;
          }
          if (patch.notes !== undefined) body.notes = patch.notes.trim();
          if (patch.address !== undefined) body.address = patch.address.trim();
          if (patch.scopeType !== undefined) body.scopeType = patch.scopeType;
          if (patch.districtKeys !== undefined) body.districtKeys = patch.districtKeys;
          if (patch.mandalRefs !== undefined) body.mandalRefs = patch.mandalRefs;
          if (patch.villageRefs !== undefined) body.villageRefs = patch.villageRefs;
          if (patch.zoneIds !== undefined) body.zoneIds = patch.zoneIds;
          if (patch.sortOrder !== undefined) body.sortOrder = patch.sortOrder;
          if (Object.keys(body).length === 0) return;
          await updateStoreYardApi(id, body);
          await refreshStoreYardsFromApi();
          showToast("Store / yard updated.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not update store yard.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        storeYards: prev.storeYards.map((s) => {
          if (s.id !== id) return s;
          return {
            ...s,
            ...patch,
            name: patch.name !== undefined ? patch.name.trim() || s.name : s.name,
            notes: patch.notes !== undefined ? patch.notes.trim() : s.notes,
            address: patch.address !== undefined ? patch.address.trim() : s.address,
            scopeType: patch.scopeType ?? s.scopeType,
            districtKeys: patch.districtKeys ?? s.districtKeys,
            mandalRefs: patch.mandalRefs ?? s.mandalRefs,
            villageRefs: patch.villageRefs ?? s.villageRefs,
            zoneIds: patch.zoneIds ?? s.zoneIds,
          };
        }),
      }));
      showToast("Store / yard updated.", "success");
    },
    [patchRoot, refreshStoreYardsFromApi, showToast],
  );

  const removeStoreYard = useCallback(
    async (id: string): Promise<boolean> => {
      if (storeYardsApiActiveRef.current) {
        try {
          await deleteStoreYardApi(id);
          await refreshStoreYardsFromApi();
          showToast("Store / yard removed.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove store yard.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({ storeYards: prev.storeYards.filter((s) => s.id !== id) }));
      showToast("Store / yard removed.", "success");
      return true;
    },
    [patchRoot, refreshStoreYardsFromApi, showToast],
  );

  const moveStoreYard = useCallback(
    async (id: string, direction: "up" | "down") => {
      if (storeYardsApiActiveRef.current) {
        try {
          const prev = rootRef.current;
          const sorted = [...prev.storeYards].sort((a, b) => a.sortOrder - b.sortOrder);
          const idx = sorted.findIndex((s) => s.id === id);
          if (idx < 0) {
            showToast("Store yard not found.", "error");
            return;
          }
          const j = direction === "up" ? idx - 1 : idx + 1;
          if (j < 0 || j >= sorted.length) {
            showToast("Cannot move this store further.", "error");
            return;
          }
          const reordered = [...sorted];
          [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
          const orderedIds = reordered.map((s) => s.id);
          const list = await reorderStoreYards(orderedIds);
          setRoot((p) => finalizeDraft({ ...p, storeYards: list }));
          showToast("Store order updated.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not reorder stores.", "error");
        }
        return;
      }
      let moved = false;
      patchRoot((prev) => {
        const sorted = [...prev.storeYards].sort((a, b) => a.sortOrder - b.sortOrder);
        const idx = sorted.findIndex((s) => s.id === id);
        if (idx < 0) return {};
        const j = direction === "up" ? idx - 1 : idx + 1;
        if (j < 0 || j >= sorted.length) return {};
        moved = true;
        const reordered = [...sorted];
        [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
        const withOrder = reordered.map((s, i) => ({ ...s, sortOrder: i + 1 }));
        const byId = new Map(withOrder.map((s) => [s.id, s]));
        return {
          storeYards: prev.storeYards.map((s) => byId.get(s.id) ?? s),
        };
      });
      showToast(
        moved ? "Store order updated." : "Cannot move this store further.",
        moved ? "success" : "error",
      );
    },
    [patchRoot, showToast],
  );

  const addConstructionTimelinePhase = useCallback(
    async (input: { title: string; approxDays: number }) => {
      const title = input.title.trim();
      if (!title) {
        showToast("Enter a level name.", "error");
        return "";
      }
      const approxDays = Math.max(0, Math.floor(Number(input.approxDays)) || 0);
      if (timelineApiActiveRef.current) {
        try {
          const row = await apiCreateConstructionTimelinePhase({ title, approxDays });
          await refreshTimelineFromApi();
          showToast("Timeline level added.", "success");
          return String(row.id);
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not add timeline level.", "error");
          return "";
        }
      }
      const id = `ctp-${Date.now()}`;
      patchRoot((prev) => {
        const sortOrder = prev.constructionTimelinePhases.reduce((m, p) => Math.max(m, p.sortOrder), 0) + 1;
        const row: ConstructionTimelinePhase = {
          id,
          title,
          approxDays,
          sortOrder,
          createdAt: new Date().toISOString(),
        };
        return { constructionTimelinePhases: [...prev.constructionTimelinePhases, row] };
      });
      showToast("Timeline level added (offline).", "success");
      return id;
    },
    [patchRoot, refreshTimelineFromApi, showToast],
  );

  const updateConstructionTimelinePhase = useCallback(
    async (
      id: string,
      patch: Partial<Pick<ConstructionTimelinePhase, "title" | "approxDays" | "sortOrder">>,
    ) => {
      if (timelineApiActiveRef.current) {
        try {
          const current = rootRef.current.constructionTimelinePhases.find((p) => p.id === id);
          if (!current) {
            showToast("Timeline level not found.", "error");
            return;
          }
          const title = patch.title !== undefined ? patch.title.trim() || current.title : current.title;
          const approxDays =
            patch.approxDays !== undefined
              ? Math.max(0, Math.floor(Number(patch.approxDays)) || 0)
              : current.approxDays;
          const sortOrder = patch.sortOrder !== undefined ? patch.sortOrder : current.sortOrder;
          await updateConstructionTimelinePhaseApi(id, { title, approxDays, sortOrder });
          await refreshTimelineFromApi();
          showToast("Timeline level saved.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not save timeline level.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        constructionTimelinePhases: prev.constructionTimelinePhases.map((p) => {
          if (p.id !== id) return p;
          const title = patch.title !== undefined ? patch.title.trim() || p.title : p.title;
          const approxDays =
            patch.approxDays !== undefined ? Math.max(0, Math.floor(Number(patch.approxDays)) || 0) : p.approxDays;
          return { ...p, ...patch, title, approxDays };
        }),
      }));
      showToast("Timeline level saved (offline).", "success");
    },
    [patchRoot, refreshTimelineFromApi, showToast],
  );

  const removeConstructionTimelinePhase = useCallback(
    async (id: string): Promise<boolean> => {
      if (timelineApiActiveRef.current) {
        try {
          await deleteConstructionTimelinePhaseApi(id);
          await refreshTimelineFromApi();
          patchRoot((prev) => ({
            milestoneTemplates: prev.milestoneTemplates.map((t) => ({
              ...t,
              linkedConstructionTimelinePhaseIds: t.linkedConstructionTimelinePhaseIds.filter((x) => x !== id),
            })),
            applications: prev.applications.map((a) => {
              const map = a.constructionTimelineActualDays;
              if (!map || !(id in map)) return a;
              const { [id]: _removed, ...rest } = map;
              return {
                ...a,
                constructionTimelineActualDays: Object.keys(rest).length ? rest : undefined,
              };
            }),
          }));
          showToast("Timeline level removed.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove timeline level.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({
        constructionTimelinePhases: prev.constructionTimelinePhases.filter((p) => p.id !== id),
        milestoneTemplates: prev.milestoneTemplates.map((t) => ({
          ...t,
          linkedConstructionTimelinePhaseIds: t.linkedConstructionTimelinePhaseIds.filter((x) => x !== id),
        })),
        applications: prev.applications.map((a) => {
          const map = a.constructionTimelineActualDays;
          if (!map || !(id in map)) return a;
          const { [id]: _removed, ...rest } = map;
          return {
            ...a,
            constructionTimelineActualDays: Object.keys(rest).length ? rest : undefined,
          };
        }),
      }));
      showToast("Timeline level removed (offline).", "success");
      return true;
    },
    [patchRoot, refreshTimelineFromApi, showToast],
  );

  const moveConstructionTimelinePhase = useCallback(
    async (id: string, direction: "up" | "down") => {
      if (timelineApiActiveRef.current) {
        try {
          const prev = rootRef.current;
          const sorted = [...prev.constructionTimelinePhases].sort((a, b) => a.sortOrder - b.sortOrder);
          const idx = sorted.findIndex((p) => p.id === id);
          if (idx < 0) {
            showToast("Timeline level not found.", "error");
            return;
          }
          const j = direction === "up" ? idx - 1 : idx + 1;
          if (j < 0 || j >= sorted.length) {
            showToast("Cannot move this level further.", "error");
            return;
          }
          const reordered = [...sorted];
          [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
          const orderedIds = reordered.map((p) => Number(p.id));
          await reorderConstructionTimelinePhases(orderedIds);
          await refreshTimelineFromApi();
          showToast("Timeline order updated.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not reorder timeline.", "error");
        }
        return;
      }
      let moved = false;
      patchRoot((prev) => {
        const sorted = [...prev.constructionTimelinePhases].sort((a, b) => a.sortOrder - b.sortOrder);
        const idx = sorted.findIndex((p) => p.id === id);
        if (idx < 0) return {};
        const j = direction === "up" ? idx - 1 : idx + 1;
        if (j < 0 || j >= sorted.length) return {};
        moved = true;
        const reordered = [...sorted];
        [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
        const withOrder = reordered.map((p, i) => ({ ...p, sortOrder: i + 1 }));
        const byId = new Map(withOrder.map((p) => [p.id, p]));
        return {
          constructionTimelinePhases: prev.constructionTimelinePhases.map((p) => byId.get(p.id) ?? p),
        };
      });
      showToast(
        moved ? "Timeline order updated (offline)." : "Cannot move this level further.",
        moved ? "success" : "error",
      );
    },
    [patchRoot, refreshTimelineFromApi, showToast],
  );

  const addStandardResource = useCallback(
    async (input: { title: string; notes?: string }) => {
      const title = input.title.trim();
      if (!title) {
        showToast("Enter a resource title.", "error");
        return "";
      }
      if (standardResourcesApiActiveRef.current) {
        try {
          const row = await createStandardResourceApi({
            title,
            notes: (input.notes ?? "").trim(),
          });
          await refreshStandardResourcesFromApi();
          showToast("Standard resource created.", "success");
          return row.id;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not create resource.", "error");
          return "";
        }
      }
      const id = `sr-${Date.now()}`;
      patchRoot((prev) => {
        const sortOrder = prev.standardResources.reduce((m, r) => Math.max(m, r.sortOrder), 0) + 1;
        const row: StandardResource = {
          id,
          title,
          notes: (input.notes ?? "").trim(),
          sortOrder,
          items: [],
          createdAt: new Date().toISOString(),
        };
        return { standardResources: [...prev.standardResources, row] };
      });
      showToast("Standard resource created.", "success");
      return id;
    },
    [patchRoot, refreshStandardResourcesFromApi, showToast],
  );

  const updateStandardResource = useCallback(
    async (id: string, patch: Partial<Pick<StandardResource, "title" | "notes" | "sortOrder">>) => {
      if (standardResourcesApiActiveRef.current) {
        try {
          const current = rootRef.current.standardResources.find((r) => r.id === id);
          if (!current) {
            showToast("Standard resource not found.", "error");
            return;
          }
          const title = patch.title !== undefined ? patch.title.trim() || current.title : current.title;
          const notes = patch.notes !== undefined ? patch.notes.trim() : current.notes;
          const sortOrder = patch.sortOrder !== undefined ? patch.sortOrder : current.sortOrder;
          await updateStandardResourceApi(id, { title, notes, sortOrder });
          await refreshStandardResourcesFromApi();
          showToast("Standard resource saved.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not save resource.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        standardResources: prev.standardResources.map((r) => {
          if (r.id !== id) return r;
          return {
            ...r,
            ...patch,
            title: patch.title !== undefined ? patch.title.trim() || r.title : r.title,
            notes: patch.notes !== undefined ? patch.notes.trim() : r.notes,
          };
        }),
      }));
      showToast("Standard resource saved.", "success");
    },
    [patchRoot, refreshStandardResourcesFromApi, showToast],
  );

  const removeStandardResource = useCallback(
    async (id: string): Promise<boolean> => {
      if (standardResourcesApiActiveRef.current) {
        try {
          await deleteStandardResourceApi(id);
          const list = await fetchStandardResources();
          setRoot((prev) =>
            finalizeDraft({
              ...prev,
              standardResources: list,
              milestoneTemplates: prev.milestoneTemplates.map((t) => ({
                ...t,
                linkedResourceIds: t.linkedResourceIds.filter((x) => x !== id),
              })),
            }),
          );
          showToast("Standard resource removed.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove resource.", "error");
          return false;
        }
      }
      patchRoot((prev) => ({
        standardResources: prev.standardResources.filter((r) => r.id !== id),
        milestoneTemplates: prev.milestoneTemplates.map((t) => ({
          ...t,
          linkedResourceIds: t.linkedResourceIds.filter((x) => x !== id),
        })),
      }));
      showToast("Standard resource removed.", "success");
      return true;
    },
    [patchRoot, refreshStandardResourcesFromApi, showToast],
  );

  const moveStandardResource = useCallback(
    async (id: string, direction: "up" | "down") => {
      if (standardResourcesApiActiveRef.current) {
        try {
          const prev = rootRef.current;
          const sorted = [...prev.standardResources].sort((a, b) => a.sortOrder - b.sortOrder);
          const idx = sorted.findIndex((r) => r.id === id);
          if (idx < 0) {
            showToast("Standard resource not found.", "error");
            return;
          }
          const j = direction === "up" ? idx - 1 : idx + 1;
          if (j < 0 || j >= sorted.length) {
            showToast("Cannot move this resource further.", "error");
            return;
          }
          const reordered = [...sorted];
          [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
          const orderedIds = reordered.map((r) => r.id);
          const list = await reorderStandardResources(orderedIds);
          setRoot((p) => finalizeDraft({ ...p, standardResources: list }));
          showToast("Resource order updated.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not reorder resources.", "error");
        }
        return;
      }
      let moved = false;
      patchRoot((prev) => {
        const sorted = [...prev.standardResources].sort((a, b) => a.sortOrder - b.sortOrder);
        const idx = sorted.findIndex((r) => r.id === id);
        if (idx < 0) return {};
        const j = direction === "up" ? idx - 1 : idx + 1;
        if (j < 0 || j >= sorted.length) return {};
        moved = true;
        const reordered = [...sorted];
        [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
        const withOrder = reordered.map((r, i) => ({ ...r, sortOrder: i + 1 }));
        const byId = new Map(withOrder.map((r) => [r.id, r]));
        return {
          standardResources: prev.standardResources.map((r) => byId.get(r.id) ?? r),
        };
      });
      showToast(
        moved ? "Resource order updated." : "Cannot move this resource further.",
        moved ? "success" : "error",
      );
    },
    [patchRoot, showToast],
  );

  const addStandardResourceItem = useCallback(
    async (
      resourceId: string,
      item: {
        label: string;
        quantity: number;
        unit: string;
        rate?: number;
        hsnCode?: string;
        catalogStandardItemId?: string | null;
      },
    ) => {
      const label = item.label.trim();
      const unit = item.unit.trim();
      if (!label || !unit) {
        showToast("Enter label and unit for the line item.", "error");
        return false;
      }
      const quantity = Math.max(0, Number(item.quantity)) || 0;
      const lineRate = Math.max(0, Number(item.rate)) || 0;
      const lineHsn = String(item.hsnCode ?? "")
        .trim()
        .replace(/\s+/g, "")
        .slice(0, 40);
      const catalogStandardItemId = item.catalogStandardItemId?.trim() || null;
      const res = rootRef.current.standardResources.find((r) => r.id === resourceId);
      if (
        catalogStandardItemId &&
        res?.items.some((x) => x.catalogStandardItemId === catalogStandardItemId)
      ) {
        showToast("This catalogue item is already on the resource.", "error");
        return false;
      }
      if (standardResourcesApiActiveRef.current) {
        try {
          await createStandardResourceItemApi(resourceId, {
            label,
            quantity,
            unit,
            rate: lineRate,
            hsnCode: lineHsn,
            catalogStandardItemId,
          });
          await refreshStandardResourcesFromApi();
          showToast("Line item added.", "success");
          return true;
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not add line item.", "error");
          return false;
        }
      }
      let added = false;
      patchRoot((prev) => ({
        standardResources: prev.standardResources.map((r) => {
          if (r.id !== resourceId) return r;
          added = true;
          const newItem: StandardResourceItem = {
            id: `sri-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            label,
            quantity,
            unit,
            rate: lineRate,
            hsnCode: lineHsn,
            catalogStandardItemId,
          };
          return { ...r, items: [...r.items, newItem] };
        }),
      }));
      if (added) showToast("Line item added.", "success");
      else showToast("Standard resource not found.", "error");
      return added;
    },
    [patchRoot, refreshStandardResourcesFromApi, showToast],
  );

  const updateStandardResourceItem = useCallback(
    async (
      resourceId: string,
      itemId: string,
      patch: Partial<Pick<StandardResourceItem, "label" | "quantity" | "unit" | "rate" | "hsnCode">>,
    ) => {
      if (standardResourcesApiActiveRef.current) {
        try {
          const res = rootRef.current.standardResources.find((r) => r.id === resourceId);
          const it = res?.items.find((x) => x.id === itemId);
          if (!res || !it) {
            showToast("Line item not found.", "error");
            return;
          }
          const label = patch.label !== undefined ? patch.label.trim() || it.label : it.label;
          const unit = patch.unit !== undefined ? patch.unit.trim() || it.unit : it.unit;
          const quantity =
            patch.quantity !== undefined ? Math.max(0, Number(patch.quantity)) || 0 : it.quantity;
          const rate =
            patch.rate !== undefined ? Math.max(0, Number(patch.rate)) || 0 : it.rate ?? 0;
          const hsnCode =
            patch.hsnCode !== undefined
              ? String(patch.hsnCode).trim().replace(/\s+/g, "").slice(0, 40)
              : it.hsnCode ?? "";
          await updateStandardResourceItemApi(resourceId, itemId, { label, unit, quantity, rate, hsnCode });
          await refreshStandardResourcesFromApi();
          showToast("Line item saved.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not save line item.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        standardResources: prev.standardResources.map((r) => {
          if (r.id !== resourceId) return r;
          return {
            ...r,
            items: r.items.map((item) => {
              if (item.id !== itemId) return item;
              const label = patch.label !== undefined ? patch.label.trim() || item.label : item.label;
              const u = patch.unit !== undefined ? patch.unit.trim() || item.unit : item.unit;
              const quantity =
                patch.quantity !== undefined ? Math.max(0, Number(patch.quantity)) || 0 : item.quantity;
              const rateVal =
                patch.rate !== undefined ? Math.max(0, Number(patch.rate)) || 0 : item.rate ?? 0;
              const hsnCode =
                patch.hsnCode !== undefined
                  ? String(patch.hsnCode).trim().replace(/\s+/g, "").slice(0, 40)
                  : item.hsnCode ?? "";
              return { ...item, ...patch, label, unit: u, quantity, rate: rateVal, hsnCode };
            }),
          };
        }),
      }));
      showToast("Line item saved.", "success");
    },
    [patchRoot, refreshStandardResourcesFromApi, showToast],
  );

  const removeStandardResourceItem = useCallback(
    async (resourceId: string, itemId: string) => {
      if (standardResourcesApiActiveRef.current) {
        try {
          await deleteStandardResourceItemApi(resourceId, itemId);
          await refreshStandardResourcesFromApi();
          showToast("Line item removed.", "success");
        } catch (e) {
          showToast(e instanceof Error ? e.message : "Could not remove line item.", "error");
        }
        return;
      }
      patchRoot((prev) => ({
        standardResources: prev.standardResources.map((r) =>
          r.id === resourceId ? { ...r, items: r.items.filter((it) => it.id !== itemId) } : r
        ),
      }));
      showToast("Line item removed.", "success");
    },
    [patchRoot, refreshStandardResourcesFromApi, showToast],
  );

  const value = useMemo(
    () => ({
      applications: root.applications,
      subcontractorMasters: root.subcontractorMasters,
      engineerMasters: root.engineerMasters,
      agentMasters: root.agentMasters,
      milestoneTemplates: root.milestoneTemplates,
      geoZones: root.geoZones,
      storeYards: root.storeYards,
      constructionTimelinePhases: root.constructionTimelinePhases,
      standardResources: root.standardResources,
      getById,
      getSubcontractorMaster,
      getEngineerMaster,
      getAgentMaster,
      getMilestoneTemplate,
      getStandardResource,
      updateApplication,
      updateHo,
      updateValidations,
      setHoldReasons,
      confirmSeeding,
      saveIciciDetails,
      addWorkDoneBill,
      confirmBillSeeding,
      updateSubcontractor,
      finalizeSubcontractor,
      updateEngineer,
      finalizeEngineer,
      addSubcontractorMaster,
      updateSubcontractorMaster,
      deleteSubcontractorMaster,
      addEngineerMaster,
      updateEngineerMaster,
      deleteEngineerMaster,
      addAgentMaster,
      updateAgentMaster,
      deleteAgentMaster,
      sendAgentOnboardingEmail,
      addMilestoneTemplate,
      updateMilestoneTemplate,
      removeMilestoneTemplate,
      addMilestoneTemplateItem,
      removeMilestoneTemplateItem,
      setMilestonePhaseComplete,
      setMilestoneItemBillLink,
      setMilestoneItemDone,
      addGeoZone,
      updateGeoZone,
      removeGeoZone,
      addStoreYard,
      updateStoreYard,
      removeStoreYard,
      moveStoreYard,
      addConstructionTimelinePhase,
      updateConstructionTimelinePhase,
      removeConstructionTimelinePhase,
      moveConstructionTimelinePhase,
      addStandardResource,
      updateStandardResource,
      removeStandardResource,
      moveStandardResource,
      addStandardResourceItem,
      updateStandardResourceItem,
      removeStandardResourceItem,
      refreshApplicationsFromApi,
    }),
    [
      root.applications,
      root.subcontractorMasters,
      root.engineerMasters,
      root.agentMasters,
      root.milestoneTemplates,
      root.geoZones,
      root.storeYards,
      root.constructionTimelinePhases,
      root.standardResources,
      getById,
      getSubcontractorMaster,
      getEngineerMaster,
      getAgentMaster,
      getMilestoneTemplate,
      getStandardResource,
      updateApplication,
      updateHo,
      updateValidations,
      setHoldReasons,
      confirmSeeding,
      saveIciciDetails,
      addWorkDoneBill,
      confirmBillSeeding,
      updateSubcontractor,
      finalizeSubcontractor,
      updateEngineer,
      finalizeEngineer,
      addSubcontractorMaster,
      updateSubcontractorMaster,
      deleteSubcontractorMaster,
      addEngineerMaster,
      updateEngineerMaster,
      deleteEngineerMaster,
      addAgentMaster,
      updateAgentMaster,
      deleteAgentMaster,
      sendAgentOnboardingEmail,
      addMilestoneTemplate,
      updateMilestoneTemplate,
      removeMilestoneTemplate,
      addMilestoneTemplateItem,
      removeMilestoneTemplateItem,
      setMilestonePhaseComplete,
      setMilestoneItemBillLink,
      setMilestoneItemDone,
      addGeoZone,
      updateGeoZone,
      removeGeoZone,
      addStoreYard,
      updateStoreYard,
      removeStoreYard,
      moveStoreYard,
      addConstructionTimelinePhase,
      updateConstructionTimelinePhase,
      removeConstructionTimelinePhase,
      moveConstructionTimelinePhase,
      addStandardResource,
      updateStandardResource,
      removeStandardResource,
      moveStandardResource,
      addStandardResourceItem,
      updateStandardResourceItem,
      removeStandardResourceItem,
      refreshApplicationsFromApi,
    ]
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error("useAppState must be used within AppStateProvider");
  return ctx;
}
